import React, { useState, useEffect } from "react";
import {
  AbsoluteCenter,
  ChakraProvider,
  Box,
  Card,
  Container,
  Center,
  Stack,
  Divider,
  Button,
  Text,
  Heading,
  VStack,
  Image,
  Grid,
  GridItem,
  Spinner,
  Stepper,
  Step,
  StepIndicator,
  StepIcon,
  StepNumber,
  Radio,
  RadioGroup,
  useColorModeValue,
} from "@chakra-ui/react";
import { useNavigate, useParams } from "react-router-dom";
import { ColorModeSwitcher } from "../ColorModeSwitcher";
import { Nav } from "../containers/Nav";
import { submitBrandCampaign, getBrandCampaign } from "../actions/campaigns";

function Step1(props) {
  const {
    brandCampaign,
    currentQuestion,
    setCurrentQuestion,
    startCountDown,
    setUserEval,
  } = props;

  return (
    <Box>
      <Center>
        <Text as="b" color="blue.500">
          You will have <Text as="u">8 minutes</Text> to complete this campaign
        </Text>
      </Center>

      <Divider my="4" color="gray.400" />

      <Center>
        <Box>
          <Image
            src={"https://hubitprototype.herokuapp.com" + brandCampaign.cover}
            fallbackSrc="https://via.placeholder.com/350x200"
            boxSize="200px"
            alt={`${brandCampaign.brand} campaign cover image`}
            borderRadius="lg"
            width="100%"
            objectFit="cover"
          />
        </Box>
      </Center>

      <Box>
        <Text as="b" mr="3">
          Production Company:
        </Text>
      </Box>
      <Box mb="2">
        <Text as="span">{brandCampaign.brand}</Text>
      </Box>
      <Box>
        <Text as="b" mr="3">
          Campaign:
        </Text>
      </Box>
      <Box mb="2">
        <Text as="span">{brandCampaign.name}</Text>
      </Box>
      <Box>
        <Text as="b" mr="3">
          Medium:
        </Text>
      </Box>
      <Box mb="2">
        <Text as="span">{brandCampaign.medium}</Text>
      </Box>
      <Box>
        <Text as="b" mr="3">
          Description:
        </Text>
      </Box>
      <Box mb="2">
        <Text as="span">{brandCampaign.description}</Text>
      </Box>
      <Box>
        <Text as="b" mr="3">
          Genre:
        </Text>
      </Box>
      <Box mb="2">
        <Text as="span">{brandCampaign.genre}</Text>
      </Box>
      <Box>
        <Text as="b" mr="3">
          Questions:
        </Text>
      </Box>
      <Box mb="2">
        <Text as="span">
          {brandCampaign?.buckets &&
            Object.keys(brandCampaign?.buckets[0]).length - 3}
        </Text>
      </Box>

      <Divider my="4" color="gray.400" />

      <Center>
        <Button
          mx="auto"
          py="0"
          height="35px"
          variant="solid"
          colorScheme="teal"
          onClick={() => {
            setCurrentQuestion(currentQuestion + 1);
            startCountDown();
          }}
        >
          Begin Evaluation
        </Button>
      </Center>
    </Box>
  );
}

function SubmissionSlide(props) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { brandCampaign, userEval } = props;
  const navigate = useNavigate();

  useEffect(() => {
    if (isSubmitting) {
      return;
    }
    setIsSubmitting(true);
    submitBrandCampaign({
      ...userEval,
      brand: brandCampaign.brand,
      campaign: brandCampaign.name,
    }).then((evaluation) => {
      setTimeout(() => {
        setIsSubmitting(false);
        return navigate(`/brand/${brandCampaign.brand}`);
      }, 4000);
    });
  });

  return (
    <Container>
      <Center>
        <Text as="b" fontSize="lg">
          Submitting Your Evaluation
        </Text>
      </Center>
      <Center>
        <Text my="5" fontSize="md">
          Please wait one moment.
        </Text>
      </Center>
      <Center>
        <Text my="5" as="i" fontSize="md">
          You will be redirected to the homepage when complete.
        </Text>
      </Center>
    </Container>
  );
}

function QuestionAnswer(props) {
  const {
    brandCampaign,
    currentQuestion,
    setCurrentQuestion,
    timer,
    userEval,
    setUserEval,
  } = props;
  const buckets = brandCampaign?.buckets ? brandCampaign?.buckets[0] : {};
  const keys = Object.keys(buckets);

  const [questionHeading, setQuestionHeading] = useState("");
  const [questionBody, setQuestionBody] = useState("");
  const [questionAnswered, setQuestionAnswered] = useState(false);
  const [userAnswer, setUserAnswer] = useState("");

  const section = keys[currentQuestion];
  const options = buckets[section];

  useEffect(() => {
    switch (section) {
      case "messageTaglines":
        setQuestionHeading("Message Taglines");
        break;
      case "conceptStoryLines":
        setQuestionHeading("Concept/Story");
        break;
      case "themeLinks":
      case "themeImages":
        setQuestionHeading("Art/Theme");
        break;
      case "castLinks":
      case "castImages":
        setQuestionHeading("Cast");
        break;
      case "influencerLinks":
        setQuestionHeading("Influencers");
        break;
      case "socialLinks":
      case "socialImages":
        setQuestionHeading("Culture");
        break;
      default:
        setQuestionHeading(section);
        break;
    }
    setQuestionBody("Select the one that you like the most:");
  }, [currentQuestion]);

  if (!buckets[keys[currentQuestion]]) {
    return (
      <SubmissionSlide userEval={userEval} brandCampaign={brandCampaign} />
    );
  } else if (
    (buckets && keys.length && buckets[keys[currentQuestion]].length === 0) ||
    !Array.isArray(buckets[keys[currentQuestion]])
  ) {
    setCurrentQuestion(currentQuestion + 1);
  }

  return (
    <Box position="relative">
      <Box top="0" right="0" position="absolute">
        <Text
          fontSize={timer?.startsWith("0") ? "2xl" : "xl"}
          as="b"
          color={timer?.startsWith("0") ? "red.400" : "blue.500"}
        >
          {timer}
        </Text>
      </Box>

      <Heading fontSize="xl">{questionHeading}</Heading>
      <Divider my="4" color="gray.400" />
      <Text as="b">{questionBody}</Text>
      <Divider my="4" color="gray.400" />

      <Stack direction="column">
        <RadioGroup
          onChange={(e) => {
            setQuestionAnswered(true);
            setUserAnswer(e);
          }}
          value={userAnswer}
        >
          {options?.length > 0 &&
            Array.isArray(options) &&
            options.map((option, idx) => {
              return (
                <Box mb="3">
                  <Radio value={option}>
                    {option.includes("/api/file") ? (
                      <Image
                        src={"https://hubitprototype.herokuapp.com" + option}
                        fallbackSrc="https://via.placeholder.com/350x200"
                        boxSize="200px"
                        alt={`${option} campaign cover image`}
                        borderRadius="lg"
                        width="100%"
                        objectFit="cover"
                      />
                    ) : (
                      option
                    )}
                  </Radio>
                </Box>
              );
            })}
        </RadioGroup>
      </Stack>
      <Divider my="4" color="gray.400" />

      <Center mt="4">
        <Button
          onClick={() => {
            setUserEval({ ...userEval, [section]: userAnswer });
            setCurrentQuestion(currentQuestion + 1);
            setUserAnswer("");
            setQuestionAnswered(false);
          }}
          isDisabled={!questionAnswered}
          mx="auto"
          py="0"
          height="35px"
          variant="solid"
          colorScheme="teal"
        >
          Submit Answer
        </Button>
      </Center>
    </Box>
  );
}

function CampaignEvaluation() {
  const navigate = useNavigate();
  const params = useParams();
  const brand = params.brand;
  const campaign = params.campaign;

  const [brandCampaign, setBrandCampaign] = useState({});
  const [currentQuestion, setCurrentQuestion] = useState(-1);

  const [userEval, setUserEval] = useState({});

  const [timer, setTimer] = useState();

  const submitEvaluation = () => {};

  useEffect(() => {
    getBrandCampaign(brand, campaign).then((campaign) => {
      if (!campaign) {
        return navigate("/home");
      }
      setBrandCampaign(campaign);
    });
  }, []);

  const startCountDown = () => {
    const token = localStorage
      .getItem("jwtToken")
      .split("Bearer ")[1]
      .split(".")[0];

    let expiresAt = new Date(new Date().getTime() + 8 * 60000).getTime();
    const hasTimer = localStorage.getItem(token + "-" + campaign);

    if (hasTimer) {
      expiresAt = hasTimer;
    } else {
      localStorage.setItem(token + "-" + campaign, expiresAt);
    }

    const countdown = setInterval(() => {
      // Get today's date and time
      const now = new Date().getTime();

      // Find the distance between now and the count down date
      const distance = expiresAt - now;

      // Time calculations for days, hours, minutes and seconds
      const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((distance % (1000 * 60)) / 1000);

      // Display the result in the element with id="demo"
      setTimer(minutes + ":" + (seconds < 10 ? "0" + seconds : seconds));

      // If the count down is finished
      if (distance < 0) {
        submitEvaluation();
        clearInterval(countdown);
      }
    }, 1000);
  };

  return (
    <Grid
      templateAreas={`"header header" "main main" "footer footer"`}
      gridTemplateRows={"50px 1fr 30px"}
      gridTemplateColumns={"150px 1fr"}
      h="200px"
      color="blackAlpha.700"
    >
      <Nav />

      <GridItem pl="2" area={"main"}>
        <VStack>
          <Box mt="8">
            <Heading
              color={useColorModeValue("gray.1000", "gray.100")}
              fontSize="2xl"
            >
              Campaign Evaluation
            </Heading>
          </Box>

          <Card
            mx="5"
            my="5"
            p="8"
            minW="2xl"
            bg={useColorModeValue("gray.100", "gray.900")}
          >
            {currentQuestion === -1 && (
              <Step1
                setUserEval={setUserEval}
                brandCampaign={brandCampaign}
                setCurrentQuestion={setCurrentQuestion}
                currentQuestion={currentQuestion}
                startCountDown={startCountDown}
              />
            )}

            {currentQuestion > -1 && (
              <QuestionAnswer
                timer={timer}
                userEval={userEval}
                setUserEval={setUserEval}
                brandCampaign={brandCampaign}
                currentQuestion={currentQuestion}
                setCurrentQuestion={setCurrentQuestion}
              />
            )}
          </Card>
        </VStack>
      </GridItem>

      {/*
      <GridItem area={'footer'}>
        <Box
          p="30px"
          width="100%"
          bg={useColorModeValue('gray.600', 'gray.600')}
          color={useColorModeValue('gray.100', 'gray.100')}
        >
          <Center>
            <Text>Copyright © {new Date().getFullYear()} Hubit</Text>
          </Center>
        </Box>
      </GridItem>
      */}
    </Grid>
  );
}

export default CampaignEvaluation;
